import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../../app/appUtils";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import { FuelExpense } from "../@types";
import { useCreateDispatchMutation } from "../dispatchApiSlice";
import ReviewModal from "./ReviewModal";
import StepIndicator from "./StepIndicator";
import DriverCrewStep from "./steps/DriverCrewStep";
import ExpensesStep from "./steps/ExpensesStep";
import IncomeStep from "./steps/IncomeStep";
import TripDetailsStep from "./steps/TripDetailsStep";

const createDispatchSchema = z.object({
  fromLocation: z.string().nonempty("Start location is required"),
  toLocation: z.string().nonempty("End location is required"),
  approximateDistance: z.number().min(0, "Distance must be positive"),
  totalExpectedIncome: z.number().min(0, "Income must be positive"),
  totalIncomeEarned: z.number().min(0, "Income must be positive"),
  totalPassengers: z.number().min(0, "Passengers must be positive"),
  perPassengerRate: z.number().min(0, "Rate must be positive"),
  driverId: z.string().nonempty("Driver is required"),
  vehicleId: z.string().nonempty("Vehicle is required"),
});

type CreateDispatchFormData = z.infer<typeof createDispatchSchema>;

interface CrewMemberInput {
  crewType: string;
  id: string;
  name: string;
}

interface OtherExpense {
  description: string;
  amount: number;
}

interface IncomeInput {
  description: string;
  expectedAmount: number;
  earnedAmount: number;
}

interface CreateDispatchViewProps {
  vehicleId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const STEPS = [
  { number: 1, label: "Trip Details" },
  { number: 2, label: "Driver & Crew" },
  { number: 3, label: "Expenses" },
  { number: 4, label: "Income" },
];

const CreateDispatchView = ({
  vehicleId,
  onCancel,
  onSuccess,
}: CreateDispatchViewProps) => {
  const toast = useToast();
  const [createDispatch, { isLoading: isCreating }] =
    useCreateDispatchMutation();

  const [currentStep, setCurrentStep] = useState(1);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Form state
  const [crewMembers, setCrewMembers] = useState<CrewMemberInput[]>([
    { crewType: "", id: "", name: "" },
  ]);
  const [fuelExpenses, setFuelExpense] = useState<FuelExpense>({
    amount: 0,
    quantity: 0,
    fuelType: "",
    fuelDate: new Date().toISOString().split("T")[0],
    stationName: "",
    pricePerUnit: 0,
    odometerReading: 0,
  });
  const [otherExpenses, setOtherExpenses] = useState<OtherExpense[]>([
    { description: "", amount: 0 },
  ]);
  const [incomes, setIncomes] = useState<IncomeInput[]>([
    { description: "", expectedAmount: 0, earnedAmount: 0 },
  ]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm<CreateDispatchFormData>({
    resolver: zodResolver(createDispatchSchema),
    defaultValues: {
      fromLocation: "",
      toLocation: "",
      approximateDistance: 0,
      driverId: "",
      vehicleId: vehicleId,
      totalExpectedIncome: 0,
      totalIncomeEarned: 0,
      totalPassengers: 0,
      perPassengerRate: 0,
    },
  });

  useEffect(() => {
    if (vehicleId) {
      setValue("vehicleId", vehicleId);
    }
  }, [vehicleId, setValue]);

  const handleNextStep = async () => {
    if (currentStep === 1) {
      const isValid = await trigger([
        "fromLocation",
        "toLocation",
        // "estimatedTime",
        "approximateDistance",
        "totalExpectedIncome",
        "totalIncomeEarned",
        "totalPassengers",
        "perPassengerRate",
      ]);
      if (isValid) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      const driverId = watch("driverId");
      if (!driverId) {
        toast.error({
          title: "Error",
          message: "Please select a driver",
        });
        return;
      }
      setCurrentStep(3);
    } else if (currentStep === 3) {
      setCurrentStep(4);
    } else if (currentStep === 4) {
      setShowReviewModal(true);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateDispatch = async () => {
    try {
      const formData = watch();

      const payload: any = {
        fromLocation: formData.fromLocation,
        toLocation: formData.toLocation,
        driverId: formData.driverId,
        vehicleId: formData.vehicleId,
        approximateDistance: formData.approximateDistance,
        totalExpectedIncome: formData.totalExpectedIncome,
        totalIncomeEarned: formData.totalIncomeEarned,
        totalPassengers: formData.totalPassengers,
        perPassengerRate: formData.perPassengerRate,
        // estimatedTime: parseInt(formData.estimatedTime),
        crews: crewMembers.length > 0 ? crewMembers : undefined,
        fuelExpense: {
          ...fuelExpenses,
          fuelDate:
            fuelExpenses.fuelDate || new Date().toISOString().split("T")[0],
        },
        expenses: otherExpenses.length > 0 ? otherExpenses : undefined,
        incomes:
          incomes.length > 0
            ? incomes.map((inc) => ({
                description: inc.description,
                expectedAmount: inc.expectedAmount,
                earnedAmount: inc.earnedAmount,
              }))
            : undefined,
      };

      await createDispatch(payload).unwrap();

      setShowReviewModal(false);
      setShowSuccess(true);
    } catch (err) {
      const errorMessage =
        getErrorDetails(err).message ||
        "Failed to create dispatch. Please try again.";

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    onSuccess();
  };

  if (showSuccess) {
    return (
      <div className="bg-white rounded-[10px] p-6">
        <SuccessView
          handleCloseModal={handleCloseSuccess}
          heading=""
          message="Dispatch has been created successfully."
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[10px] overflow-hidden">
      <StepIndicator steps={STEPS} currentStep={currentStep} />

      <form onSubmit={handleSubmit(handleNextStep)}>
        {currentStep === 1 && (
          <TripDetailsStep
            control={control}
            errors={errors}
            onNext={handleNextStep}
            onCancel={onCancel}
          />
        )}

        {currentStep === 2 && (
          <DriverCrewStep
            control={control}
            errors={errors}
            crewMembers={crewMembers}
            setCrewMembers={setCrewMembers}
            setValue={setValue}
            onNext={handleNextStep}
            onBack={handlePrevStep}
          />
        )}

        {currentStep === 3 && (
          <ExpensesStep
            fuelExpenses={fuelExpenses}
            setFuelExpenses={setFuelExpense}
            otherExpenses={otherExpenses}
            setOtherExpenses={setOtherExpenses}
            onNext={handleNextStep}
            onBack={handlePrevStep}
          />
        )}

        {currentStep === 4 && (
          <IncomeStep
            control={control}
            incomes={incomes}
            errors={errors}
            setIncomes={setIncomes}
            onSubmit={handleNextStep}
            onBack={handlePrevStep}
          />
        )}
      </form>

      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        formData={watch()}
        crewMembers={crewMembers}
        fuelExpenses={fuelExpenses}
        driverId={watch("driverId")}
        otherExpenses={otherExpenses}
        incomes={incomes}
        onProceed={handleCreateDispatch}
        isCreating={isCreating}
      />
    </div>
  );
};

export default CreateDispatchView;
